import { useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';

import type { InteractiveSubActivityFragment } from '../../lesson-builder.graphql.generated';
import SubActivityView from '../../components/sub-activity/sub-activity-view';
import ProgressIndicator from 'approot/shared/progress-bar/progress-indicator';
import { ErrorMessageActionable } from 'approot/shared/error-message/error-message-actionable';
import { green6, grey6, purple5 } from 'approot/shared/styles/colours.style';
import { useActivityBuilderPresentContext } from '../../present/activity-builder-present.context';
import { ComponentMultipleChoice } from '../../components/component.constants';
import { Box, BoxList } from 'approot/shared/design-system/box/box.style';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import { ClassroomAnswerInstruction } from '../classroom-answer.style';
import {
  ClassResponseListContent,
  ClassResponseListItem,
  ClassResponseListItemStatus,
  ClassResponseListItemText,
} from '../class-responses.style';
import Bouncer from 'approot/shared/bouncer/bouncer';
import {
  ClassAnswerNoStudents,
  ClassAnswerNoAnswers,
} from '../class-answer-empty';
import { MEDIA_MD, useMediaQuery } from 'approot/shared/style';
import { ImageThumbnailStatic } from 'approot/shared/images/images';
import {
  ACTIVITIES_ANSWER_MODE_FREE,
  ACTIVITIES_RESPONSE_VARIANT_IMAGE,
  ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE,
} from 'global/constants';
import { useGetClassStudents } from '../classroom-student.hooks';
import { prepareClassResults } from './multiple-choice-class-responses.utils';
import { MultipleChoiceCheckAnswer } from 'approot/activity-builder/components/multiple-choice/multiple-choice-check-answer.style';
import {
  CheckAnswerButtonPrimary,
  CheckAnswerSecondaryButton,
} from 'approot/activity-builder/check-answer/check-answer.style';
import { useGetStudentAnswersPollingFallback } from '../use-get-student-answers';

type Props = {
  component: Omit<ComponentMultipleChoice, '__typename'>;
  subActivityProperties: InteractiveSubActivityFragment | null;
};

const MultipleChoiceClassResponses = ({
  component,
  subActivityProperties,
}: Props) => {
  const [showAnswers, setShowAnswers] = useState<boolean>(false);

  const {
    properties: { multipleChoice },
  } = component;

  const isImage = multipleChoice.variant === ACTIVITIES_RESPONSE_VARIANT_IMAGE;
  const isTextAndImage =
    multipleChoice.variant === ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE;
  const isFree = multipleChoice.answerMode === ACTIVITIES_ANSWER_MODE_FREE;

  const { userClassId } = useActivityBuilderPresentContext();

  const isMediaUpMd = useMediaQuery(MEDIA_MD);

  const {
    data: studentsData,
    loading: studentsLoading,
    error: studentsError,
  } = useGetClassStudents({
    classId: Number(userClassId) || 1,
  });

  const studentsInClass = useMemo(
    () => studentsData?.getClassStudents || [],
    [studentsData]
  );

  const { data, error: studentAnswersError } =
    useGetStudentAnswersPollingFallback({
      userClassId: Number(userClassId),
      componentId: component.id,
    });

  const sortedOptions = useMemo(
    () => sortBy(multipleChoice.options, 'sort'),
    [multipleChoice.options]
  );

  const classResponses = useMemo(() => {
    return prepareClassResults({
      studentAnswers: data?.getClassStudentsAnswers || [],
      options: multipleChoice.options,
    });
  }, [data, multipleChoice.options]);

  return (
    <div>
      {studentsLoading || !data ? (
        <Bouncer />
      ) : studentAnswersError || studentsError ? (
        <ErrorMessageActionable ariaPage="Class Responses" paddingTop={0} />
      ) : studentsInClass.length === 0 ? (
        <ClassAnswerNoStudents />
      ) : data?.getClassStudentsAnswers.length === 0 ? (
        <ClassAnswerNoAnswers />
      ) : (
        <ClassroomAnswerInstruction>
          <SubActivityView subActivityDetails={subActivityProperties}>
            <BoxList>
              {sortedOptions.map((option, index) => (
                <ClassResponseListItem key={option.itemId}>
                  <Box $gap={12} $alignItems="center">
                    {(isImage || isTextAndImage) && (
                      <ImageThumbnailStatic
                        imageUrl={option.image?.original || ''}
                        width={80}
                      />
                    )}
                    <ClassResponseListContent>
                      <ClassResponseListItemText>
                        {option.text && <span>{option.text}</span>}
                        <ClassResponseListItemStatus>
                          {showAnswers &&
                            !isFree &&
                            option.correctAnswerBoolean && (
                              <ActivityBuilderInlineSvg
                                id="icon_correct"
                                width={isMediaUpMd ? 32 : 24}
                                height={isMediaUpMd ? 32 : 24}
                              />
                            )}
                          <span>{classResponses[index].total}</span>
                        </ClassResponseListItemStatus>
                      </ClassResponseListItemText>
                      <ProgressIndicator
                        animate
                        progress={classResponses[index].total}
                        total={studentsInClass.length}
                        colour={
                          isFree || !showAnswers
                            ? purple5
                            : option.correctAnswerBoolean
                              ? green6
                              : grey6
                        }
                        height={isMediaUpMd ? 24 : 16}
                      />
                    </ClassResponseListContent>
                  </Box>
                </ClassResponseListItem>
              ))}
            </BoxList>
          </SubActivityView>
        </ClassroomAnswerInstruction>
      )}
      {!isFree &&
        data?.getClassStudentsAnswers &&
        data.getClassStudentsAnswers.length > 0 && (
          <MultipleChoiceCheckAnswer>
            {showAnswers ? (
              <CheckAnswerSecondaryButton
                type="button"
                onClick={() => setShowAnswers(false)}
              >
                Hide Answer
              </CheckAnswerSecondaryButton>
            ) : (
              <CheckAnswerButtonPrimary
                type="button"
                onClick={() => setShowAnswers(true)}
              >
                Show Answer
              </CheckAnswerButtonPrimary>
            )}
          </MultipleChoiceCheckAnswer>
        )}
    </div>
  );
};

export default MultipleChoiceClassResponses;
